import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Info, AlertTriangle, AlertOctagon, UserPlus, ShieldQuestion, Clock, KeyRound, Wrench } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const SNOOZE_OPTIONS = [
  { label: "1 hour",       hours: 1 },
  { label: "4 hours",      hours: 4 },
  { label: "Tomorrow 9am", hours: null, whenTomorrow9: true },
  { label: "Next Monday",  hours: null, whenNextMonday: true },
];

function computeSnoozeHours(opt) {
  if (opt.hours != null) return opt.hours;
  const now = new Date();
  const target = new Date(now);
  if (opt.whenTomorrow9) { target.setDate(target.getDate() + 1); target.setHours(9, 0, 0, 0); }
  if (opt.whenNextMonday) {
    const daysUntilMonday = (8 - target.getDay()) % 7 || 7;
    target.setDate(target.getDate() + daysUntilMonday);
    target.setHours(9, 0, 0, 0);
  }
  return Math.max(0.5, (target.getTime() - now.getTime()) / 3600000);
}

const ICONS = {
  status: { info: Info, warning: AlertTriangle, critical: AlertOctagon },
  assignment: UserPlus,
  request: ShieldQuestion,
  // Iter 91o — Network Assessment Keys need review / broken flag
  network_key_needs_review: KeyRound,
  network_key_broken:       Wrench,
};

function iconFor(n) {
  if (n.type === "status") return (ICONS.status[n.severity] || Info);
  return ICONS[n.type] || Info;
}
const COLOR = { info: "text-sky-500", warning: "text-amber-500", critical: "text-red-500" };

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread_count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.notification_id}/read`); } catch { /* ignore */ }
    }
    setOpen(false);
    load();
    // Iter 115r — never navigate into apps that aren't live yet (Helpdesk is
    // still a roadmap item). The notification is simply marked as read.
    if (n.link && !n.link.startsWith("/helpdesk")) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
          <Bell className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5">
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          {unread > 0 && (
            <button data-testid="notification-mark-all" onClick={markAll} className="flex items-center gap-1 text-xs text-primary hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-3 py-8 text-center text-sm text-slate-400" data-testid="notification-empty">You&apos;re all caught up.</div>
          ) : items.map((n) => {
            const Icon = iconFor(n);
            const snooze = async (opt) => {
              try {
                const hours = computeSnoozeHours(opt);
                await api.post(`/notifications/${n.notification_id}/snooze`, { hours, label: opt.label });
                toast.success(`Reminder set for ${opt.label.toLowerCase()}`);
                setItems((cur) => cur.filter((x) => x.notification_id !== n.notification_id));
              } catch (e) { toast.error("Snooze failed"); }
            };
            return (
              <div key={n.notification_id} data-testid={`notification-item-${n.notification_id}`}
                className={`flex items-start gap-3 border-b border-slate-50 px-3 py-2.5 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-sky-50/40"}`}>
                <button onClick={() => onItem(n)} className="flex min-w-0 flex-1 items-start gap-3 text-left">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${n.type === "status" ? (COLOR[n.severity] || "text-sky-500") : "text-slate-400"}`} />
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${n.read ? "text-slate-600" : "font-semibold text-slate-800"}`}>{n.title}</div>
                    {n.body && <div className="text-xs text-slate-400 line-clamp-2">{n.body}</div>}
                    <div className="mt-0.5 text-[11px] text-slate-300">{timeAgo(n.created_at)}</div>
                  </div>
                </button>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid={`notification-snooze-${n.notification_id}`} title="Remind me later"
                      className="shrink-0 rounded p-1 text-slate-400 opacity-0 transition-opacity hover:bg-slate-100 hover:text-primary group-hover:opacity-100"
                      onClick={(e) => e.stopPropagation()}
                      style={{ opacity: 1 }}>
                      <Clock className="h-3.5 w-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-44 p-1">
                    <div className="mb-1 px-2 pt-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">Remind me in…</div>
                    {SNOOZE_OPTIONS.map((opt) => (
                      <button key={opt.label} onClick={() => snooze(opt)} data-testid={`notification-snooze-${n.notification_id}-${opt.label.replace(/\s+/g, "-").toLowerCase()}`}
                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-100">
                        <Clock className="h-3.5 w-3.5 text-slate-400" /> {opt.label}
                      </button>
                    ))}
                  </PopoverContent>
                </Popover>
                {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-sky-500" />}
              </div>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
